import {
  IMfaChallenge,
  IMfaErrorEnvelope,
  ISignInResponse,
  MfaChallengeExpiredCode,
  MfaChallengeLockedCode,
  MfaCodeInvalidCode,
  MfaCodeNotSentCode,
  MfaResendThrottledCode,
  MfaSendFailedCode
} from '@app/src/common/model/MfaChallenge';

// Decision logic for the login verification step, deliberately free of any runtime dependency
// (no stores, no repository, no react-intl) so it can be unit-tested under Jest's node environment.

export const MFA_CODE_LENGTH = 6;

export type MfaTerminalState = 'expired' | 'locked';

export interface IMfaInlineError {
  message: string;
  params?: Record<string, any>;
}

export interface IMfaErrorResolution {
  // Inline message rendered under the code input.
  error?: IMfaInlineError;
  terminal?: MfaTerminalState;
  clearCode?: boolean;
  // Select the current value so the next keystroke replaces it.
  selectCode?: boolean;
  // The server has confirmed that no code exists for this challenge, so the step must stop
  // offering a code entry until a resend delivers one.
  codeNotSent?: boolean;
  // Make resend available right away.
  resendAvailable?: boolean;
  // Restart the resend countdown from this many seconds.
  resendAfterSeconds?: number;
  // Fall back to the existing login toast instead of an inline message.
  notify?: 'auth' | 'network';
}

export type SignInOutcome =
  | { kind: 'challenge'; challenge: IMfaChallenge }
  | { kind: 'token'; accessToken: string; refreshToken: string }
  | { kind: 'unknown' };

// Digits only, leading zeros preserved, capped at the expected length.
export const sanitizeCode = (value: string): string =>
  (value || '').replace(/\D/g, '').slice(0, MFA_CODE_LENGTH);

// "0:47", "1:00", "10:00". Minutes are not zero-padded, unlike DateTimeService.msToOptionalHHMMSS.
export const formatMinuteSeconds = (totalSeconds: number): string => {
  const safe =
    Number.isFinite(totalSeconds) && totalSeconds > 0
      ? Math.floor(totalSeconds)
      : 0;
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Distinguishes a reported timing from an absent one. Defaulting a missing value to zero would
// silently kill a live challenge: an expiry of "now" makes the next tick declare it expired.
export const toOptionalSeconds = (value?: number | null): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? Math.floor(value)
    : undefined;

// Whole minutes of remaining validity, rounded up, as shown in the delivery hint.
export const toValidityMinutes = (remainingSeconds: number): number =>
  Number.isFinite(remainingSeconds) && remainingSeconds > 0
    ? Math.ceil(remainingSeconds / 60)
    : 0;

// Which sentence the step leads with. "No code" outranks the other two, which both claim a code was
// sent. The key is reused from the UM0044 outcome: the string is verbatim what is needed here, and a
// second copy under an `info` name would be one more thing to keep in sync across both locales.
export const resolveMfaInfoMessage = (
  codeSent: boolean,
  showValidity: boolean
): string => {
  if (!codeSent) {
    return 'auth.mfa.error.notSent';
  }
  return showValidity ? 'auth.mfa.hint' : 'auth.mfa.hint.short';
};

// Validity after a resend. A resend issues a new code, so a validity that has already elapsed cannot
// describe it: drop it to null and leave expiry to the server, rather than let the next tick expire a
// code emailed a second ago. A validity still running is kept — the new code inherits the challenge's
// lifetime — and a reported timing always wins.
export const nextExpiresAt = (
  currentExpiresAt: number | null,
  reportedSeconds: number | undefined,
  now: number
): number | null => {
  if (reportedSeconds !== undefined && reportedSeconds > 0) {
    return now + reportedSeconds * 1000;
  }
  return currentExpiresAt !== null && currentExpiresAt <= now
    ? null
    : currentExpiresAt;
};

// Sign-in has two successful outcomes: the usual tokens, or a verification challenge. A challenge
// never carries tokens, so `mfaRequired` and a challenge id together are the reliable signal.
export const resolveSignInOutcome = (
  payload?: ISignInResponse | null
): SignInOutcome => {
  const challengeId = payload?.challengeId;
  const hasChallengeId = typeof challengeId === 'string' && !!challengeId;

  if (
    hasChallengeId &&
    (payload!.mfaRequired === true || !payload!.access_token)
  ) {
    return {
      kind: 'challenge',
      challenge: {
        mfaRequired: true,
        challengeId: challengeId as string,
        maskedEmail: payload!.maskedEmail || '',
        challengeExpiresIn: toSeconds(payload!.challengeExpiresIn),
        resendAfter: toSeconds(payload!.resendAfter),
        // Absent means sent: only an explicit false may strip the step of its code entry.
        codeSent: payload!.codeSent !== false,
        deliveryFailed: payload!.deliveryFailed === true
      }
    };
  }

  if (payload?.access_token && payload?.refresh_token) {
    return {
      kind: 'token',
      accessToken: payload.access_token,
      refreshToken: payload.refresh_token
    };
  }

  return { kind: 'unknown' };
};

export const mapMfaError = (
  envelope: IMfaErrorEnvelope | undefined | null,
  hasResponse: boolean
): IMfaErrorResolution => {
  if (!hasResponse) {
    return { notify: 'network' };
  }

  switch (envelope?.Code) {
    case MfaCodeInvalidCode: {
      const attempts = envelope?.Data?.AttemptsRemaining;
      // Without the attempt count the sentence would render broken, so fall back to the toast.
      if (!Number.isFinite(attempts)) {
        return { notify: 'auth' };
      }
      return {
        error: { message: 'auth.mfa.error.invalid', params: { n: attempts } },
        selectCode: true
      };
    }

    case MfaChallengeExpiredCode:
      return {
        error: { message: 'auth.mfa.error.expired' },
        terminal: 'expired',
        clearCode: true
      };

    case MfaChallengeLockedCode:
      // The value is kept — only the account-independent attempt limit was reached.
      return {
        error: { message: 'auth.mfa.error.locked' },
        terminal: 'locked'
      };

    case MfaResendThrottledCode: {
      const wait = envelope?.Data?.RetryAfterSeconds;
      return {
        error: { message: 'auth.mfa.error.throttled' },
        resendAfterSeconds: Number.isFinite(wait) ? Number(wait) : undefined
      };
    }

    case MfaCodeNotSentCode:
      // Nothing was ever delivered, so the entered value cannot belong to this challenge: drop it,
      // move the step into its no-code state — which says so itself, instead of repeating the
      // sentence as a field error — and arm the resend at once, the only way forward.
      return {
        codeNotSent: true,
        clearCode: true,
        resendAvailable: true
      };

    case MfaSendFailedCode:
      // No cooldown was started, so another attempt may follow immediately.
      return {
        error: { message: 'auth.mfa.error.sendFailed' },
        resendAvailable: true
      };

    default:
      return { notify: 'auth' };
  }
};

function toSeconds(value?: number): number {
  return Number.isFinite(value) && (value as number) > 0
    ? Math.floor(value as number)
    : 0;
}
